import _ from "@/boot/lodash";

export enum SupportedLocaleCodes {
  BG = "bg", // Bulgarian
  DA = "da", // Danish
  DE = "de", // German
  EL = "el", // Greek
  EN = "en", // English
  ES = "es", // Spanish
  ES_419 = "es_419", // Latin American Spanish
  FR = "fr", // French
  HE = "he", // Hebrew
  IN = "id", // Indonesian
  IT = "it", // Italian
  JA = "ja", // Japanese
  NB = "nb", // Norwegian Bokmål
  PL = "pl", // Polish
  PT = "pt", // Portuguese
  RU = "ru" // Russian
}

/**
 * @name getLocaleCountryCode
 * @desc This function checks native support for a given locale code. If
 * unsupported, it matches and checks the ISO-3166-1 country designator to give a
 * better fallback for regional variations – eg `es_CL (Spanish, Chile)` would
 * match and return the "es" locale. Failing that, "en" is returned as a default.
 * See: https://en.wikipedia.org/wiki/ISO_3166-1 (Countries)
 * See: https://en.wikipedia.org/wiki/ISO_3166-2 (Principal Subdivisions)
 * @param {string} val Any ISO-3166 code (with optional regional ISO-3166-2 designator)
 * @returns {SupportedLocaleCodes} An Upmind supported ISO-3166 locale code
 **/

export function getSupportedLocaleCode(val: string) {
  const localeCode = `${val}`.replace("-", "_");
  const supportedCodes = _.values(SupportedLocaleCodes);
  // If supported, return raw locale code
  if (supportedCodes.includes(localeCode as any))
    return localeCode as SupportedLocaleCodes;
  // Construct regex to match country (ISO 3166-1) and regional (ISO 3166-2) designators
  const regex = new RegExp(`^([a-z]{2})(?:_|$)([a-z0-9]*)?$`, "i");
  // Run regex to return matched designators
  const [, country] = (localeCode.match(regex) as [string?, string?]) ?? [];
  // If supported, return country locale code
  if (supportedCodes.includes(country as any))
    return country as SupportedLocaleCodes;
  // Else, fallback to English (EN)
  return SupportedLocaleCodes.EN;
}

/**
 * @name getLocaleCountryCode
 * @desc This function maps ISO-639 Language Codes to ISO-3166 Country Codes.
 * See https://docs.oracle.com/cd/E13214_01/wli/docs92/xref/xqisocodes.html for
 * code tables
 * @param {string} code An 'ISO-639' language code
 * @returns {string} An 'ISO-3166' country code
 **/

export function getLocaleCountryCode(code: SupportedLocaleCodes) {
  switch (code) {
    case SupportedLocaleCodes.EL:
      return "gr";
    case SupportedLocaleCodes.EN:
      return "gb";
    case SupportedLocaleCodes.NB:
      return "no";
    default:
      return code.toLowerCase();
  }
}
